import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { UploadOutlined } from '@ant-design/icons';
import { Button, TabsProps } from 'antd';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import DeviceContainerTable from '@totem/components/devices/devicesContainer/DeviceContainerTable';
import DeviceExport from '@totem/components/devices/devicesContainer/DeviceExport';
import DevicesContainer, {
  containerConfiguration,
} from '@totem/components/devices/devicesContainer/DevicesContainer';
import DeviceSearch from '@totem/components/devices/devicesContainer/DeviceSearch';
import MaintenanceWindowButton from '@totem/components/devices/devicesContainer/MaintenanceWindowButton';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import ContentLayout from '@totem/components/ui_components/page/ContentLayout';
import DropDownFilters from '@totem/components/ui_components/reportSettings/dropDownFilters';
import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import RecordCountBadge from '@totem/components/ui_components/widgets/RecordCountBadge';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';

const styles = {
  totals: {
    fontSize: '16px',
    marginRight: '20px',
  },
  tabContainer: {
    paddingRight: '2rem',
    paddingLeft: '2rem',
  },
};

const DevicesPage = () => {
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const { userProfile } = useContext(UserProfileContext);
  const canCreateMaintenanceWindows =
    permissions.teamMember.hasPermissions(userProfile);
  const canImportSoftware =
    permissions.organizationalAdministrator.hasPermissions(userProfile);

  const getTabItems = () => {
    const tabItems: TabsProps['items'] = [
      {
        key: 'details',
        label: 'Details',
        children: (
          <>
            <DeviceSearch />
            <br />
            <DeviceContainerTable
              showMultiselect={canCreateMaintenanceWindows}
            />
          </>
        ),
      },
    ];
    return tabItems;
  };

  return (
    <ContentLayout>
      <InputContainer
        componentKey={'dashboard.devices'}
        componentLabel={'Portfolio: Devices'}
        filterConfig={containerConfiguration}
        useUrlParams
        defaultSort={{
          field: 'device.displayName',
          direction: 'ascend',
        }}
      >
        <DevicesContainer onRecordTotalChanged={setTotalRecords}>
          <IBCard isCovered title={'Device Management'}>
            <DropDownFilters
              showFilterChips
              keys={['type', 'vendor', 'region', 'building']}
              extra={''}
            />
            <IBTabs
              defaultActiveKey="Report"
              tabBarExtraContent={
                <span>
                  <RecordCountBadge count={totalRecords} />
                  {canCreateMaintenanceWindows && (
                    <>
                      &nbsp;&nbsp;&nbsp;
                      <IBTooltip content={permissions.teamMember.tooltip}>
                        <MaintenanceWindowButton />
                      </IBTooltip>
                    </>
                  )}
                  {canImportSoftware && (
                    <>
                      &nbsp;&nbsp;&nbsp;
                      <IBTooltip
                        content={
                          permissions.organizationalAdministrator.tooltip
                        }
                      >
                        <Link to="/dashboard/devices/software/import">
                          <Button icon={<UploadOutlined />}>
                            Import Software
                          </Button>
                        </Link>
                      </IBTooltip>
                    </>
                  )}
                  {canCreateMaintenanceWindows && (
                    <>
                      &nbsp;&nbsp;&nbsp;
                      <IBTooltip content={permissions.teamMember.tooltip}>
                        <DeviceExport />
                      </IBTooltip>
                    </>
                  )}
                </span>
              }
              style={styles.tabContainer}
              items={getTabItems()}
            />
          </IBCard>
        </DevicesContainer>
      </InputContainer>
    </ContentLayout>
  );
};

export default DevicesPage;
